import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function Register() {
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) return toast.error(t("consent_required"));
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, accept_terms: true });
      setUser(data);
      toast.success("Account creato!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    const redirect = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto py-16 px-6">
        <h1 className="text-3xl font-black tracking-tighter mb-8">{t("register_title")}</h1>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="label-mono">{t("name")}</Label>
            <Input data-testid="reg-name" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="rounded-none border-slate-300 focus-visible:ring-[#002FA7]" />
          </div>
          <div>
            <Label className="label-mono">{t("email")}</Label>
            <Input data-testid="reg-email" type="email" required value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="rounded-none border-slate-300 focus-visible:ring-[#002FA7]" />
          </div>
          <div>
            <Label className="label-mono">{t("password")} <span className="text-slate-400">(min 8)</span></Label>
            <Input data-testid="reg-password" type="password" required minLength={8} value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="rounded-none border-slate-300 focus-visible:ring-[#002FA7]" />
          </div>
          <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
            <Checkbox data-testid="reg-terms" checked={terms} onCheckedChange={setTerms} className="mt-0.5" />
            <span>{t("accept_terms")}</span>
          </label>
          <Button data-testid="reg-submit" type="submit" disabled={loading}
            className="w-full rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-6">
            {loading ? t("starting") : t("submit")}
          </Button>
        </form>
        <div className="flex items-center gap-4 my-6"><div className="flex-1 h-px bg-slate-200" /><span className="label-mono text-slate-400">{t("or_google")}</span><div className="flex-1 h-px bg-slate-200" /></div>
        <Button data-testid="reg-google" onClick={googleLogin} variant="outline"
          className="w-full rounded-none border-slate-900 label-mono py-6 hover:bg-slate-900 hover:text-white">
          {t("google_btn")}
        </Button>
        <p className="text-sm text-slate-600 mt-6">{t("already_have")} <Link to="/login" className="underline text-[#002FA7]">{t("nav_login")}</Link></p>
      </div>
    </Layout>
  );
}
